import { SelectOptionType } from '@aidkitorg/component-library';
import { SupportRequest } from '../../SupportUtil';

/**
 * Status options for support cases with colored dots
 */
export const STATUS_OPTIONS: SelectOptionType[] = [
  {
    label: (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-gray-500" />
        <span>Viewed</span>
      </div>
    ),
    value: 'viewed'
  },
  {
    label: (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-yellow-500" />
        <span>Open</span>
      </div>
    ),
    value: 'open'
  },
  {
    label: (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-teal-500" />
        <span>Resolved</span>
      </div>
    ),
    value: 'resolved'
  },
  {
    label: (
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-red-500" />
        <span>Escalated</span>
      </div>
    ),
    value: 'escalated'
  },
];

/**
 * Detect if a contact string is a phone number
 */
export const isPhoneContact = (contact: string): boolean => {
  return contact.startsWith('+') || /^\d+$/.test(contact);
};

/**
 * Detect if a contact string is an email
 */
export const isEmailContact = (contact: string): boolean => {
  return contact.includes('@');
};

/**
 * Get the contact type label for display
 */
export const getContactTypeLabel = (contact: string): string => {
  if (isEmailContact(contact)) {
    return 'Email Address';
  }
  if (isPhoneContact(contact)) {
    return 'Phone Number';
  }
  return 'Contact';
};

/**
 * Get primary contact method icon name for a support case
 */
export const getContactMethodIcon = (
  contactMethods: string[] | undefined | null
): 'EnvelopeIcon' | 'DevicePhoneMobileIcon' | 'PhoneIcon' => {
  if (!contactMethods || contactMethods.length === 0) return 'EnvelopeIcon';

  if (contactMethods.includes('email')) return 'EnvelopeIcon';
  if (contactMethods.includes('sms') || contactMethods.includes('mms')) return 'DevicePhoneMobileIcon';
  if (contactMethods.includes('call')) return 'PhoneIcon';

  return 'EnvelopeIcon';
};

/**
 * Get the display name or contact info for a support case
 * Returns the applicant's name if available, otherwise falls back to the source (phone/email)
 * @param request - The support request object
 * @param fallbackText - Optional fallback text when no name or source is available (defaults to 'Unknown')
 */
export const getDisplayName = (request: SupportRequest, fallbackText: string = 'Unknown'): string => {
  const { row, applicantInfos } = request;

  // Try to get name from applicantInfos
  const allApplicantInfos = Object.values(applicantInfos);
  if (allApplicantInfos.length > 0) {
    const firstInfo = allApplicantInfos[0];
    const name = firstInfo?.legal_name || firstInfo?.name || firstInfo?.first_name;
    if (name) return name;
  }

  // Fall back to source (phone/email)
  return row.source || fallbackText;
};
